import { FactionBlacklistCharacter } from "@1games-ru/region-shared/dist/types/faction/faction";
import { createColumnHelper } from "@tanstack/react-table";
import { useCallback } from "react";
import { useTranslation } from "react-i18next";
import clsx from "clsx";
import { dateFormat } from "@/core/helpers/dateFormat";
import { getDiffTimeDDHHmm } from "@/core/helpers/getDiffTimeDDHHmm";
import { Phone2Icon } from "@/shared/icons";
import Loader from "@/shared/ui/Loader";
import Corner from "@/shared/widgets/Corner";
import { IntroductionRequestRow } from "@/domains/faction/constants/introductionRequestRow";
import { ParticipantRow } from "@/domains/faction/constants/participantRow";
import { getRowGradient } from "@/domains/faction/helpers/getRowGradient";
import { useRemoveIntroductionRequest } from "@/domains/faction/hooks/useRemoveIntroductionRequest";

export default function useColleaguesColumns() {
  const { t } = useTranslation("faction");

  const { mutate: removeIntroductionRequest, isPending: isRemoveIntroductionRequest } =
    useRemoveIntroductionRequest();

  // Участники ===========================================================================
  const getParticipantsColumns = useCallback(() => {
    const columnHelper = createColumnHelper<ParticipantRow>();

    return [
      columnHelper.accessor((row) => `${row?.firstName ?? ""} ${row?.lastName ?? ""}`.trim(), {
        id: "fullName",
        header: t("faction.tableTitles.nameAndSurname"),
        cell: (info) => (
          <div>
            <Corner
              className="top-auto right-auto left-0 bottom-2.5 -rotate-180"
              classNameSvg="text-dark-900"
            />
            {info.getValue() ?? ""}
          </div>
        ),
        meta: { isSort: true, style: { minWidth: "12rem", maxWidth: "12rem" } },
      }),
      columnHelper.accessor("id", {
        header: "sID",
        cell: (info) => info.getValue(),
        meta: { isSort: false, style: { minWidth: "3.7rem" } },
      }),
      columnHelper.accessor("rankLevel", {
        header: t("faction.tableTitles.rankLvl"),
        cell: (info) => info.getValue(),
        meta: { isSort: true, style: { minWidth: "4.5rem" } },
      }),
      columnHelper.accessor("rankName", {
        header: t("faction.tableTitles.rank"),
        cell: (info) => info.getValue(),
        meta: { isSort: true, style: { minWidth: "10rem" } },
      }),
      columnHelper.accessor("onlineTimeInAWeek", {
        header: t("faction.tableTitles.online7d"),
        cell: (info) => info.getValue(),
        meta: { isSort: true, style: { minWidth: "5rem" } },
      }),
      columnHelper.accessor("dutyTimeInAWeek", {
        header: t("faction.tableTitles.workTime"),
        cell: (info) => info.getValue(),
        meta: { isSort: true, style: { minWidth: "8rem" } },
      }),
      columnHelper.accessor("createdAt", {
        header: t("faction.tableTitles.timeInFaction"),
        cell: (info) => {
          const value = info.getValue();
          return value
            ? getDiffTimeDDHHmm(
                value,
                t("faction.YY"),
                t("faction.MM"),
                t("faction.DD"),
                t("faction.HH"),
                t("faction.mm"),
              )
            : "";
        },
        meta: { isSort: true, style: { minWidth: "5rem" } },
      }),
      columnHelper.accessor("inOnline", {
        header: t("faction.tableTitles.status"),
        cell: (info) => {
          const value = info.getValue();
          return (
            <div>
              <span
                className={clsx(
                  "relative z-1 font-medium",
                  value ? "text-[#81C39A]" : "text-[#6D6D6D]",
                )}
              >
                {value ? t("faction.isOnline") : t("faction.isNotOnline")}
              </span>
              <div
                className="absolute top-0 right-0 w-25 h-[calc(100%-0.625rem)] rounded-[0.4375rem]"
                style={{ background: getRowGradient(value ? "#405C4A" : "#6D6D6D") }}
              />
              <Corner className="top-auto bottom-2.5 rotate-90" classNameSvg="text-dark-900" />
            </div>
          );
        },
        meta: { isSort: true, style: { minWidth: "4.7rem" } },
      }),
    ];
  }, [t]);

  // ЧС ==================================================================================
  const getBlackListColumns = useCallback(() => {
    const columnHelper = createColumnHelper<FactionBlacklistCharacter>();

    return [
      columnHelper.accessor((row) => `${row?.firstName ?? ""} ${row?.lastName ?? ""}`.trim(), {
        id: "fullName",
        header: t("faction.tableTitles.nameAndSurname"),
        cell: (info) => (
          <div>
            <Corner
              className="top-auto right-auto left-0 bottom-2.5 -rotate-180"
              classNameSvg="text-dark-900"
            />
            {info.getValue() ?? ""}
          </div>
        ),
        meta: { isSort: true, style: { minWidth: "12rem", maxWidth: "12rem" } },
      }),
      columnHelper.accessor("id", {
        header: "sID",
        cell: (info) => info.getValue(),
        meta: { isSort: false, style: { minWidth: "3.7rem" } },
      }),
      columnHelper.accessor("reason", {
        header: t("faction.tableTitles.reason"),
        cell: (info) => info.getValue(),
        meta: { isSort: true, style: { minWidth: "39.5rem", maxWidth: "39.5rem" } },
      }),
      columnHelper.accessor("createdAt", {
        header: t("faction.tableTitles.date"),
        cell: (info) => {
          const value = info.getValue();
          const dateAndTime = value ? dateFormat(value, true).split(" ") : [];

          return (
            <div>
              <Corner className="top-auto bottom-2.5 rotate-90" classNameSvg="text-dark-900" />
              {value ? (
                <div>
                  {dateAndTime[0]} <span className="text-white/70">{dateAndTime[1]}</span>
                </div>
              ) : (
                "-"
              )}
            </div>
          );
        },
        meta: { isSort: true, style: { minWidth: "9rem", maxWidth: "9rem" } },
      }),
    ];
  }, [t]);

  // Заявки ==============================================================================
  const getRequestsColumns = useCallback(() => {
    // TODO поправить под IntroductionRequestRow |||||
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const columnHelper = createColumnHelper<any>();

    return [
      columnHelper.accessor((row) => `${row?.firstName ?? ""} ${row?.lastName ?? ""}`.trim(), {
        id: "fullName",
        header: t("faction.tableTitles.nameAndSurname"),
        cell: (info) => (
          <div>
            <Corner
              className="top-auto right-auto left-0 bottom-2.5 -rotate-180"
              classNameSvg="text-dark-900"
            />
            {info.getValue() ?? ""}
          </div>
        ),
        meta: { isSort: true, style: { minWidth: "12rem", maxWidth: "12rem" } },
      }),
      columnHelper.accessor("id", {
        header: "sID",
        cell: (info) => info.getValue(),
        meta: { isSort: false, style: { minWidth: "3.7rem" } },
      }),
      columnHelper.accessor("createdAt", {
        header: t("faction.tableTitles.dateOfSubmission"),
        cell: (info) => {
          const value = info.getValue();
          const dateAndTime = value ? dateFormat(value, true).split(" ") : [];

          return value ? (
            <div>
              {dateAndTime[0]} <span className="text-white/70">{dateAndTime[1]}</span>
            </div>
          ) : (
            "-"
          );
        },
        meta: { isSort: true },
      }),
      columnHelper.accessor("phoneNumber", {
        header: t("faction.tableTitles.phoneNumber"),
        cell: (info) => (
          <div className="relative inline-flex min-w-[4.75rem]">
            {info.getValue()}
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
              }}
              className="absolute -top-1 -right-8 flex justify-center items-center size-6 ml-2.5 bg-white/3 rounded-xl transition-all duration-200 hover:bg-dark-700"
              data-off-hover
            >
              {isRemoveIntroductionRequest && <Loader />}
              <Phone2Icon className="size-2.5" />
            </button>
          </div>
        ),
        meta: { isSort: true },
      }),
      columnHelper.accessor("inOnline", {
        header: t("faction.tableTitles.status"),
        cell: (info) => (
          <div>
            <span
              className={clsx(
                "relative z-1 font-medium",
                info.getValue() ? "text-[#81C39A]" : "text-[#6D6D6D]",
              )}
            >
              {info.getValue() ? t("faction.isOnline") : t("faction.isNotOnline")}
            </span>
            <div
              className="absolute top-0 right-0 w-25 h-[calc(100%-0.625rem)]"
              style={{ background: getRowGradient(info.getValue() ? "#405C4A" : "#6D6D6D") }}
            />
          </div>
        ),
        meta: { isSort: true, style: { minWidth: "4rem" } },
      }),
      columnHelper.accessor("buttons", {
        header: "",
        cell: (info) => (
          <>
            <div
              className="absolute top-0 left-0 w-[calc(100%-0.6rem)] h-full bg-dark-900"
              data-off-hover
            >
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  const characterId = info?.row?.original?.characterId;
                  removeIntroductionRequest({ characterId });
                }}
                className="flex justify-center items-center w-full h-[calc(100%-0.625rem)] ml-2.5 text-12 font-bold uppercase bg-dark-800 rounded-xl transition-all duration-200 hover:bg-dark-700"
              >
                {t("faction.decline")}
              </button>
            </div>
            <Corner className="top-auto bottom-2.5 rotate-90" classNameSvg="text-dark-900" />
            <div className="absolute top-0 left-0 -translate-x-[8px] w-[8px] h-[8px] pointer-events-none z-2">
              <svg viewBox="0 0 8 8" width="8" height="8" className="w-full h-full text-dark-900">
                <defs>
                  <mask id="notch-tr" maskUnits="userSpaceOnUse">
                    <rect x="0" y="0" width="8" height="8" fill="#fff" />
                    <circle cx="0" cy="8" r="8" fill="#000" />
                  </mask>
                </defs>

                <rect x="0" y="0" width="8" height="8" fill="currentColor" mask="url(#notch-tr)" />
              </svg>
            </div>
            <div className="absolute bottom-2.5 left-0 -translate-x-[8px] w-[8px] h-[8px] pointer-events-none z-2">
              <svg viewBox="0 0 8 8" width="8" height="8" className="w-full h-full text-dark-900">
                <defs>
                  <mask id="notch-br" maskUnits="userSpaceOnUse">
                    <rect x="0" y="0" width="8" height="8" fill="#fff" />
                    <circle cx="0" cy="0" r="8" fill="#000" />
                  </mask>
                </defs>
                <rect x="0" y="0" width="8" height="8" fill="currentColor" mask="url(#notch-br)" />
              </svg>
            </div>
          </>
        ),
        meta: { isSort: false, style: { minWidth: "9.75rem" } },
      }),
    ];
  }, [isRemoveIntroductionRequest, removeIntroductionRequest, t]);

  // Отдел =============================================================================
  const getDepartamentColumns = useCallback(() => {
    const columnHelper = createColumnHelper<ParticipantRow>();

    return [
      columnHelper.accessor((row) => `${row?.firstName ?? ""} ${row?.lastName ?? ""}`.trim(), {
        id: "fullName",
        header: t("faction.tableTitles.nameAndSurname"),
        cell: (info) => (
          <div>
            <Corner
              className="top-auto right-auto left-0 bottom-2.5 -rotate-180"
              classNameSvg="text-dark-900"
            />
            {info.getValue() ?? ""}
          </div>
        ),
        meta: { isSort: true, style: { minWidth: "12rem", maxWidth: "12rem" } },
      }),
      columnHelper.accessor("id", {
        header: "sID",
        cell: (info) => info.getValue(),
        meta: { isSort: false, style: { minWidth: "3.7rem" } },
      }),
      columnHelper.accessor("rankName", {
        id: t("faction.tableTitles.rank"),
        header: "Ранг",
        cell: (info) => info.getValue(),
        meta: { isSort: true, style: { minWidth: "3.7rem" } },
      }),
      columnHelper.accessor("departmentName", {
        header: t("faction.tableTitles.department"),
        cell: (info) => info.getValue(),
        meta: { isSort: true },
      }),
      columnHelper.accessor("tagName", {
        header: t("faction.tableTitles.tag"),
        cell: (info) => info.getValue(),
        meta: { isSort: true },
      }),
      columnHelper.accessor("inOnline", {
        header: t("faction.tableTitles.status"),
        cell: (info) => (
          <div>
            <Corner className="top-auto bottom-2.5 rotate-90" classNameSvg="text-dark-900" />
            <span
              className={clsx(
                "relative z-1 font-medium",
                info.getValue() ? "text-[#81C39A]" : "text-[#6D6D6D]",
              )}
            >
              {info.getValue() ? t("faction.isOnline") : t("faction.isNotOnline")}
            </span>
            <div
              className="absolute top-0 right-0 w-25 h-[calc(100%-0.625rem)] rounded-[0.4375rem]"
              style={{ background: getRowGradient(info.getValue() ? "#405C4A" : "#6D6D6D") }}
            />
          </div>
        ),
        meta: { isSort: true, style: { minWidth: "4rem" } },
      }),
    ];
  }, [t]);

  return {
    getParticipantsColumns,
    getBlackListColumns,
    getRequestsColumns,
    getDepartamentColumns,
  };
}
